import type { FC } from 'react'

import { useEffect, useState } from 'react'
import { CheckCheck, Search } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'
import { homeFaqs } from '@/constants/home'

const senders = ['Priya, a candle brand', 'Tom, a cycling shop', 'Grace, a skincare startup', 'Jonah, a bakery', 'Elif, a fashion label', 'Sam, a tech founder']

const useTyped = (text: string, key: number): string => {
    const [count, setCount] = useState(0)

    useEffect(() => {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            setCount(text.length)

            return
        }

        setCount(0)

        const timer = window.setInterval(() => {
            setCount(value => {
                if (value >= text.length) {
                    window.clearInterval(timer)

                    return value
                }

                return Math.min(text.length, value + 2)
            })
        }, 22)

        return () => window.clearInterval(timer)
    }, [text, key])

    return text.slice(0, count)
}

const Conversation: FC<{ index: number }> = ({ index }) => {
    const faq = homeFaqs[index]
    const typed = useTyped(faq.answer, index)
    const done = typed.length >= faq.answer.length

    return (
        <div className='flex flex-col gap-4'>
            <div className='flex flex-col items-end'>
                <p className='max-w-[85%] rounded-2xl rounded-br-md bg-ink px-4 py-3 text-[15px] leading-snug text-cream'>
                    {faq.question}
                </p>
                <span className='mt-1.5 flex items-center gap-1 font-mono text-[10px] text-stone'>
                    <CheckCheck className='size-3.5 text-volt' />
                    Seen
                </span>
            </div>

            <div className='flex items-end gap-2.5'>
                <span className='flex size-8 shrink-0 items-center justify-center rounded-full bg-ember font-heading text-xs font-bold text-ink'>U</span>
                <p className='max-w-[85%] rounded-2xl rounded-bl-md bg-paper px-4 py-3 text-[15px] leading-relaxed text-ink shadow-[0_10px_24px_-18px_rgb(30_30_30/0.45)]'>
                    <span className='sr-only'>{faq.answer}</span>
                    <span aria-hidden='true'>
                        {typed}
                        {!done && <span className='ml-0.5 inline-block h-4 w-[2px] translate-y-0.5 animate-blink bg-ember' />}
                    </span>
                </p>
            </div>
        </div>
    )
}

export const AskUsAnything: FC = () => {
    const [active, setActive] = useState(0)
    const [read, setRead] = useState<Array<number>>([0])

    const open = (index: number) => {
        setActive(index)
        setRead(current => (current.includes(index) ? current : [...current, index]))
    }

    const unread = homeFaqs.length - read.length

    return (
        <section
            id='faq'
            aria-labelledby='faq-title'
            className='py-20 md:py-28'
        >
            <div className='mx-auto max-w-[1400px] px-5 md:px-10'>
                <div className='grid gap-8 lg:grid-cols-12 lg:items-end'>
                    <div className='lg:col-span-7'>
                        <SectionLabel index='17'>Questions</SectionLabel>
                        <SplitText
                            as='h2'
                            id='faq-title'
                            lines={['Ask Us Anything']}
                            italic='anything'
                            className='mt-5 text-[clamp(3rem,7vw,6.5rem)] font-bold leading-[0.88] tracking-[-0.045em]'
                        />
                    </div>
                    <Reveal
                        as='p'
                        delay={150}
                        className='text-lg leading-relaxed text-stone lg:col-span-5'
                    >
                        Our DMs, lightly tidied. These are the questions we get most, with the answers we actually send.
                    </Reveal>
                </div>

                <Reveal
                    delay={100}
                    className='mt-12'
                >
                    <div className='grid overflow-hidden rounded-[2rem] border border-ink/10 bg-paper md:rounded-[2.5rem] lg:grid-cols-[400px_1fr]'>
                        <div className='border-ink/10 lg:border-r'>
                            <div className='flex items-center justify-between px-6 pb-3 pt-6'>
                                <p className='font-heading text-xl font-bold tracking-[-0.03em]'>Requests</p>
                                <span className='rounded-full bg-ember px-2.5 py-0.5 font-mono text-[11px] text-ink'>{unread} unread</span>
                            </div>
                            <div className='mx-6 mb-3 flex items-center gap-2 rounded-xl bg-sand px-3 py-2 text-sm text-stone'>
                                <Search className='size-4' />
                                Search messages
                            </div>

                            <ul className='px-3 pb-3'>
                                {homeFaqs.map((faq, index) => {
                                    const isRead = read.includes(index)
                                    const isActive = active === index

                                    return (
                                        <li key={faq.question}>
                                            <h3>
                                                <button
                                                    type='button'
                                                    aria-expanded={isActive}
                                                    aria-controls={`faq-answer-${index} faq-pane`}
                                                    onClick={() => open(index)}
                                                    className={cn(
                                                        'flex w-full items-center gap-3 rounded-2xl p-3 text-left transition-colors',
                                                        isActive ? 'bg-sand' : 'hover:bg-sand/60'
                                                    )}
                                                >
                                                    <span className='flex size-11 shrink-0 items-center justify-center rounded-full bg-ink font-heading text-sm font-bold text-cream'>
                                                        {senders[index].charAt(0)}
                                                    </span>
                                                    <span className='min-w-0 flex-1'>
                                                        <span className='block truncate font-mono text-[10px] uppercase tracking-[0.12em] text-stone'>{senders[index]}</span>
                                                        <span className={cn('block text-[15px] leading-snug', isRead ? 'text-ink/70' : 'font-semibold text-ink')}>
                                                            {faq.question}
                                                        </span>
                                                    </span>
                                                    {!isRead && <span className='size-2.5 shrink-0 rounded-full bg-ember' />}
                                                </button>
                                            </h3>

                                            {isActive && (
                                                <div
                                                    id={`faq-answer-${index}`}
                                                    className='px-3 pb-5 pt-2 lg:hidden'
                                                >
                                                    <Conversation index={index} />
                                                </div>
                                            )}
                                        </li>
                                    )
                                })}
                            </ul>
                        </div>

                        <div className='hidden flex-col bg-cream/60 lg:flex'>
                            <div className='flex items-center gap-3 border-b border-ink/10 bg-paper px-6 py-4'>
                                <span className='flex size-10 items-center justify-center rounded-full bg-ink font-heading text-sm font-bold text-cream'>
                                    {senders[active].charAt(0)}
                                </span>
                                <div>
                                    <p className='font-semibold leading-none'>{senders[active]}</p>
                                    <p className='mt-1 font-mono text-[11px] text-stone'>Updraft usually replies within the hour</p>
                                </div>
                            </div>
                            <div
                                id='faq-pane'
                                aria-live='polite'
                                className='flex flex-1 flex-col justify-end p-8'
                            >
                                <Conversation
                                    key={active}
                                    index={active}
                                />
                            </div>
                        </div>
                    </div>
                </Reveal>
            </div>
        </section>
    )
}
